import React, { useState, useMemo } from 'react'
import styled from 'styled-components'
import { FiSearch, FiX, FiCheck, FiLayers } from 'react-icons/fi'

interface Props {
  dists: any[]
  selectedIds: number[]
  onChange: (ids: number[]) => void
  label?: string
}

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;
  margin-top: 4px;
`

const SearchWrapper = styled.div`
  position: relative;
  width: 100%;

  svg {
    position: absolute;
    left: 12px;
    top: 50%;
    transform: translateY(-50%);
    color: #94a3b8;
    font-size: 16px;
  }

  input {
    width: 100%;
    height: 38px;
    background: rgba(15, 23, 42, 0.6);
    border: 1px solid rgba(255, 255, 255, 0.12);
    border-radius: 8px;
    padding: 0 12px 0 36px;
    color: #ffffff;
    font-size: 13px;
    font-family: inherit;
    box-sizing: border-box;

    &:focus {
      outline: none;
      border-color: #38bdf8;
      background: rgba(15, 23, 42, 0.85);
    }

    &::placeholder {
      color: #64748b;
    }
  }
`

const SelectedChipsArea = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  max-height: 120px;
  overflow-y: auto;
  padding: 4px;
  background: rgba(15, 23, 42, 0.3);
  border: 1px dashed rgba(255, 255, 255, 0.1);
  border-radius: 8px;

  /* Custom scrollbar */
  &::-webkit-scrollbar {
    width: 4px;
  }
  &::-webkit-scrollbar-thumb {
    background: rgba(255, 255, 255, 0.2);
    border-radius: 4px;
  }
`

const Chip = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: rgba(14, 165, 233, 0.2);
  border: 1px solid rgba(14, 165, 233, 0.4);
  color: #38bdf8;
  font-size: 11px;
  font-weight: 600;
  padding: 4px 10px;
  border-radius: 999px;
  max-width: 250px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;

  button {
    background: transparent;
    border: none;
    color: #38bdf8;
    cursor: pointer;
    display: flex;
    align-items: center;
    padding: 0;
    font-size: 13px;

    &:hover {
      color: #f87171;
    }
  }
`

const DropdownList = styled.div`
  max-height: 150px;
  overflow-y: auto;
  background: #0f172a;
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 8px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.4);

  &::-webkit-scrollbar {
    width: 4px;
  }
  &::-webkit-scrollbar-thumb {
    background: rgba(255, 255, 255, 0.2);
    border-radius: 4px;
  }
`

const DropdownItem = styled.div<{ $selected: boolean }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 12px;
  font-size: 12px;
  cursor: pointer;
  background: ${(props) => (props.$selected ? 'rgba(56, 189, 248, 0.15)' : 'transparent')};
  color: ${(props) => (props.$selected ? '#38bdf8' : '#e2e8f0')};
  border-bottom: 1px solid rgba(255, 255, 255, 0.04);
  transition: all 0.15s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.08);
  }

  .dist-info {
    display: flex;
    flex-direction: column;
    overflow: hidden;

    .dist-name {
      font-weight: 600;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }

    .dist-sub {
      font-size: 10px;
      color: #94a3b8;
    }
  }
`

export function DistMultipleSelector({ dists, selectedIds, onChange, label }: Props) {
  const [search, setSearch] = useState('')

  const filteredDists = useMemo(() => {
    if (!search.trim()) return dists
    const term = search.toLowerCase().trim()
    return dists.filter((d) => {
      const name = String(d.name || d.fantasy_name || '').toLowerCase()
      const cnpj = String(d.cnpj || '').toLowerCase()
      return name.includes(term) || cnpj.includes(term)
    })
  }, [dists, search])

  const toggleSelect = (id: number) => {
    if (selectedIds.includes(id)) {
      onChange(selectedIds.filter((item) => item !== id))
    } else {
      onChange([...selectedIds, id])
    }
  }

  const selectedDists = useMemo(() => {
    return dists.filter((d) => selectedIds.includes(Number(d.id)))
  }, [dists, selectedIds])

  return (
    <Container>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <span style={{ fontSize: '11px', color: '#94a3b8', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
          {label || 'Distribuidores Autorizados'} ({selectedIds.length} selecionado{selectedIds.length === 1 ? '' : 's'})
        </span>
        {selectedIds.length > 0 && (
          <button
            type="button"
            onClick={() => onChange([])}
            style={{
              background: 'transparent',
              border: 'none',
              color: '#f87171',
              fontSize: '11px',
              cursor: 'pointer',
              fontWeight: 600,
            }}
          >
            Limpar seleção
          </button>
        )}
      </div>

      {selectedDists.length > 0 && (
        <SelectedChipsArea>
          {selectedDists.map((d) => (
            <Chip key={d.id} title={d.name}>
              {d.name}
              <button type="button" onClick={() => toggleSelect(Number(d.id))}>
                <FiX />
              </button>
            </Chip>
          ))}
        </SelectedChipsArea>
      )}

      <SearchWrapper>
        <FiSearch />
        <input
          type="text"
          placeholder="Buscar distribuidor por nome ou CNPJ..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </SearchWrapper>

      <DropdownList>
        {filteredDists.length === 0 ? (
          <div style={{ padding: '12px', fontSize: '12px', color: '#64748b', textAlign: 'center' }}>
            Nenhum distribuidor encontrado.
          </div>
        ) : (
          filteredDists.slice(0, 50).map((d) => {
            const isSelected = selectedIds.includes(Number(d.id))
            return (
              <DropdownItem
                key={d.id}
                $selected={isSelected}
                onClick={() => toggleSelect(Number(d.id))}
              >
                <div className="dist-info">
                  <span className="dist-name">{d.name || d.fantasy_name}</span>
                  <span className="dist-sub">{d.cnpj ? `CNPJ: ${d.cnpj}` : `ID: ${d.id}`}</span>
                </div>
                {isSelected && <FiCheck style={{ color: '#38bdf8', fontSize: '14px', flexShrink: 0 }} />}
              </DropdownItem>
            )
          })
        )}
      </DropdownList>
    </Container>
  )
}

export default DistMultipleSelector
